"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { isThetaError } from "@theta/api-client";
import { thetaApi } from "@/lib/api/theta";
import { computeAnchoredPosition, visualViewportRect } from "@/lib/anchoredPosition";
import { useSendMessageById } from "@/lib/hooks/useSendMessageById";
import { useHoverCardKeyboard } from "@/lib/hooks/useHoverCardKeyboard";
import { Avatar } from "./ui/Avatar";
import { AffiliationChip } from "./AffiliationChip";
import { BotBadge } from "./BotBadge";
import { SignInToSee } from "./SignInToSee";
import { retryUnlessRefused } from "@/lib/hooks/queryRetry";

/**
 * Where the card goes, and how tall it may be — the third field is not
 * decoration (OSO-512).
 *
 * `computeAnchoredPosition` clamps `top` so `top + maxHeight` fits the viewport.
 * A card that then renders at its natural height was clamped against a box that
 * does not exist: on a 300px viewport (iOS with the keyboard up) it sat 100px
 * below the fold with the arithmetic saying it fitted. `MentionDropdown` and
 * `EmojiSuggestionDropdown` both apply it; this card dropped it when it moved
 * onto the shared helper.
 */
export interface HoverCardCoords {
  top: number;
  left: number;
  /** Cap on the rendered height. The clamp above is computed against it. */
  maxHeight: number;
}

export const HOVER_CARD_OPEN_DELAY = 400;
export const HOVER_CARD_CLOSE_DELAY = 150;
const CARD_WIDTH = 300;
/**
 * How tall to assume the card is when picking a side. It has to be a guess:
 * the side is chosen as the card opens, before it has rendered or its profile
 * has arrived.
 *
 * Measured on stage: 256px with the "Send besked" button, 235px without — the
 * common card, not the tallest, since badges, an affiliation chip, a three-line
 * bio and both "siden" lines together reach 330–390px. 260 is the common case
 * plus a little, so a card that would only just fit below flips above instead of
 * ending flush with the edge.
 *
 * Not the card's height limit: `computeAnchoredPosition` returns the room
 * actually available below as `maxHeight`, so on a tall screen the card renders
 * at its content's height. The estimate only becomes the cap when the card flips
 * above, where a box hung from `top` cannot be taller than the height `top` was
 * computed from without covering the name (OSO-516 — the first cut applied it as
 * a hard cap everywhere, putting a scrollbar inside any fuller profile).
 */
const CARD_HEIGHT_ESTIMATE = 260;

// Only elevated roles get a badge — plain membership is already implied by the
// "Medlem af gruppen siden" line.
const ROLE_BADGES: Record<string, { label: string; className: string }> = {
  admin: {
    label: "Admin",
    className:
      "bg-primary-muted/50 text-primary-muted-foreground border-indigo-200 dark:border-indigo-800",
  },
  moderator: {
    label: "Moderator",
    className:
      "bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800",
  },
};

function formatMonthYear(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleDateString("da-DK", { month: "long", year: "numeric" });
}

/** Where the card should sit relative to whatever element triggered it —
 *  beside it on whichever side has room, clamped so it never runs off any edge.
 *
 *  This used to clamp horizontally only, on the reasoning that a card below the
 *  fold was survivable because the card was desktop-only and the page scrolls.
 *  OSO-458 ended that: the card opens on keyboard focus and holds a focusable
 *  "Send besked" button, so a keyboard user could tab into a control that was
 *  off screen — measured on stage at 191px below the fold, button included.
 *
 *  It reuses `lib/anchoredPosition.ts` rather than growing a second
 *  flip-and-clamp here, with one deliberate difference: the result is converted
 *  back to PAGE coordinates and the panel stays `position: absolute`. Client
 *  coordinates and `position: fixed` would need a scroll listener to stay glued
 *  to the trigger (what `useAnchoredPosition` provides); page coordinates scroll
 *  with the trigger for free, which is what this card already did. `PostHtml`
 *  also calls this imperatively off a raw `.mention` anchor with no React ref to
 *  hand a hook, so the pure function is the reusable half here in any case. */
export function computeHoverCardPosition(el: HTMLElement): HoverCardCoords {
  const { top, left, maxHeight } = computeAnchoredPosition({
    anchor: el.getBoundingClientRect(),
    viewport: visualViewportRect(),
    // Below the name is where this card has always sat and where readers now
    // expect it; `computeAnchoredPosition` only flips when below cannot hold
    // it and above genuinely has more room.
    preferred: "below",
    desiredHeight: CARD_HEIGHT_ESTIMATE,
    width: CARD_WIDTH,
  });
  // Client -> page coordinates, so the card scrolls with the trigger under
  // `position: absolute`. See the note above on why this does not use
  // `position: fixed`.
  return { top: top + window.scrollY, left: left + window.scrollX, maxHeight };
}

/**
 * The card itself: fetches the profile BY ID and renders it at fixed `coords`.
 * Shared by `ProfileHoverCard` (wraps a React trigger) and any caller that
 * has to trigger it from outside React — e.g. `PostHtml` delegating hover off
 * a raw `.mention` anchor inside `dangerouslySetInnerHTML`'d cooked HTML,
 * where there's no element to wrap with a trigger span.
 *
 * The read is `GET /api/v1/users/{id}`, not the legacy
 * `GET /theta/users/:username/profile`: every surface that opens this card
 * already has the id — `FeedItem.author_id`, `TopComment.user_id`, a stored
 * mention anchor's `data-user-id` — and the id is a person's only address
 * (O-21a: ten accounts carry a numeric username equal to a *different* user's
 * id, so a handle cannot be resolved). `inGroup` makes the answer contextual,
 * as `category_id` did on the old route.
 */
export function ProfileHoverCardPanel({
  userId,
  categoryId,
  coords,
  onMouseEnter,
  onMouseLeave,
  panelRef,
  onMissing,
}: {
  userId: number;
  // Group the card is opened from; makes the card contextual — the affiliation
  // chip only shows when the user admins an ancestor of this group, and the
  // role badge / "member since" reflect this group's membership.
  categoryId?: number;
  coords: HoverCardCoords;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  /** So a keyboard-driven trigger can find what is focusable inside the card.
   *  Optional: `PostHtml` renders this panel directly, off a raw `.mention`
   *  anchor, and has no trigger component to co-ordinate with. */
  panelRef?: React.Ref<HTMLDivElement>;
  /**
   * Called once the lookup has established that this person is gone, so the
   * caller can stop the anchor that opened the card from navigating to a page
   * that will 404 (OSO-620). Optional: a card opened somewhere with no anchor
   * behind it has nothing to fix.
   */
  onMissing?: (userId: number) => void;
}) {
  // Prefixed `["v1", "user", id]` so the profile PAGE's invalidation of that
  // key also refreshes any card open for the same person.
  const { data: profile, isLoading, isError, error } = useQuery({
    queryKey: ["v1", "user", userId, categoryId ?? null],
    queryFn: () =>
      // `GroupRef` is `id | slug`; a bare number is the BRANDED group id, so
      // the plain one from the legacy wire goes over as its string form —
      // which is what the client does with it anyway.
      thetaApi.user(userId, categoryId != null ? { inGroup: String(categoryId) } : {}),
    staleTime: 5 * 60 * 1000,
    // A 404 is a real answer — missing, deleted and staged are deliberately
    // one answer (D7) — and so is a signed-out 401 (OSO-695), which draws the
    // sign-in card below: neither is a transport failure worth retrying.
    retry: retryUnlessRefused,
  });

  // "Send besked" (OSO-339): the hover card had no such button before, only
  // the full profile page did. `can.message` is the server's own verdict — the
  // same rule `POST /conversations` enforces — so the button is drawn from the
  // document already fetched, and the second call only decides where it goes.
  const canMessage = profile?.can.message ?? false;
  const { isRequest, dmError, dmBusy, handleSendMessage } = useSendMessageById(
    userId,
    canMessage,
  );

  /**
   * A PERSON WHO IS NO LONGER THERE STILL GETS AN ANSWER — OSO-620.
   *
   * This used to `return null`, so hovering a mention of a deleted account did
   * nothing at all: no card, no error, no movement. Nothing was broken, but
   * silence is indistinguishable from broken, and that is what was reported.
   *
   * A 404 here is deliberately ONE answer for three cases — no such id, a
   * staged invite placeholder, and a deleted row (D7). So the card must say
   * the same thing for all three and must not hint at which it got. "Findes ikke længere" is true of every one
   * of them from the reader's side.
   *
   * `onMissing` hands the same fact to `PostHtml`, which uses it to stop the
   * anchor navigating to a profile page that will 404.
   */
  /**
   * A 404 FROM A GROUP-SCOPED LOOKUP IS NOT PROOF THE PERSON IS GONE.
   *
   * `GET /users/{id}?inGroup=…` also answers 404 when the GROUP cannot be
   * resolved or read (`resolveScopeGroup`, backend/src/routes/v1/users.ts), so
   * treating every 404 as "deleted" would tell a reader that a live person no
   * longer exists — and strip the link to them. The old code merely rendered
   * nothing, which was silent but never wrong.
   *
   * So when the first lookup was group-scoped, ask again without the scope and
   * believe only that answer. The unscoped 404 is the one that means the user.
   */
  const scoped404 = isThetaError(error, "notFound") && categoryId != null;
  const { isError: goneUnscoped } = useQuery({
    queryKey: ["v1", "user", userId, null],
    queryFn: () => thetaApi.user(userId, {}),
    enabled: scoped404,
    staleTime: 5 * 60 * 1000,
    retry: retryUnlessRefused,
  });

  const isGone = categoryId == null
    ? isThetaError(error, "notFound")
    : scoped404 && goneUnscoped;
  useEffect(() => {
    if (isGone) onMissing?.(userId);
  }, [isGone, onMissing, userId]);

  if (isGone) {
    return (
      <div
        ref={panelRef}
        /**
         * THE SAME POSITIONING EVERY OTHER RETURN PATH CARRIES. Both callers
         * portal this into `document.body`, so without it the card is a static
         * block at the foot of the page rather than a card beside the mention —
         * which reads, from the outside, exactly like the "hover does nothing"
         * this change is fixing.
         */
        style={{
          position: "absolute",
          top: coords.top,
          left: coords.left,
          width: CARD_WIDTH,
        }}
        className="z-[200]"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div className="bg-surface rounded-2xl border border-border shadow-xl p-4">
        <p className="text-sm font-medium text-foreground">
          Denne bruger findes ikke længere
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Navnet står stadig i teksten, fordi det blev skrevet dengang kontoen
          fandtes.
        </p>
        </div>
      </div>
    );
  }

  /**
   * SIGNED OUT, THE PROFILE IS SIGNED-IN ONLY (OSO-695) — and the card must
   * say so, like every other people-surface. This used to fall into the
   * `return null` below: hover showed the skeleton for a beat and then the
   * card vanished, which reads as broken. The reader hovering here is real —
   * a public event's discussion is readable signed out and every author name
   * in it is a trigger — so they get the same "Log ind for at se …" answer
   * the member grid and the attendee sheet give, in the card's own frame.
   */
  if (isThetaError(error, "unauthenticated")) {
    return (
      <div
        ref={panelRef}
        // Same positioning as every other return path — see the note above.
        style={{
          position: "absolute",
          top: coords.top,
          left: coords.left,
          width: CARD_WIDTH,
        }}
        className="z-[200]"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <div className="bg-surface rounded-2xl border border-border shadow-xl p-4">
          <SignInToSee what="profilen" className="py-0 text-left" />
        </div>
      </div>
    );
  }

  if (isError) return null;

  const membership = profile?.membershipInGroup ?? null;
  const roleBadge = membership ? ROLE_BADGES[membership.role] : undefined;
  // Inside a group, how long they have been in *this* group is the relevant
  // fact; the site-wide join date is only a fallback (e.g. no membership).
  const groupSince = formatMonthYear(membership?.joinedAt ?? null);
  const memberSince = groupSince ? null : formatMonthYear(profile?.createdAt ?? null);
  const affiliation = profile?.affiliation ?? null;

  return (
    <div
      ref={panelRef}
      /**
       * `dialog`, not `tooltip` — a tooltip may not contain anything focusable,
       * and this card carries a "Send besked" button whenever `can.message`.
       * OSO-458 made that button reachable, so the contradiction stopped being
       * latent: Tab moves focus in, Shift+Tab moves it back, Escape closes and
       * returns focus to the trigger. That is a dialog's contract.
       *
       * `components/ui/Tooltip.tsx` is the genuine article and stays as it is —
       * `pointer-events-none`, a plain text label, nothing to focus.
       *
       * Non-modal: nothing outside is inert and the card closes on Escape or
       * when focus leaves, so `aria-modal` stays at its default false.
       *
       * Not done here: telling the trigger to announce that a card opened
       * (`aria-haspopup` / `aria-expanded`). The trigger is a wrapper `<span>`
       * around the focusable element rather than the element itself, so those
       * attributes would sit on the wrong node — putting them right means
       * changing what this component asks of its callers.
       */
      role="dialog"
      aria-label={profile?.name ? `Profilkort for ${profile.name}` : "Profilkort"}
      /**
       * `maxHeight` + `overflow-y: auto` is what makes the clamp true: the
       * caller has already guaranteed `top + maxHeight` is on screen, and this
       * is the half that holds the card to it. Without the cap a tall card
       * simply overflows the bottom edge and the clamp measured a box nobody
       * rendered (OSO-512).
       */
      style={{
        position: "absolute",
        top: coords.top,
        left: coords.left,
        width: CARD_WIDTH,
        maxHeight: coords.maxHeight,
        overflowY: "auto",
      }}
      className="z-[200] overscroll-contain"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="bg-surface rounded-2xl border border-border overflow-hidden shadow-xl">
        {isLoading || !profile ? (
          <div className="animate-pulse">
            <div className="h-20 bg-muted" />
            <div className="px-4 pb-4">
              <div className="w-16 h-16 rounded-full bg-gray-300 dark:bg-gray-600 -mt-8 border-4 border-surface" />
              <div className="h-4 bg-muted rounded w-32 mt-3" />
              <div className="h-3 bg-muted rounded w-24 mt-2" />
            </div>
          </div>
        ) : (
          <>
            <div className="relative h-20 bg-gradient-to-br from-indigo-400 via-purple-400 to-pink-400">
              {profile.coverUrl && (
                <img
                  src={profile.coverUrl}
                  alt=""
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            <div className="px-4 pb-4">
              <div className="relative -mt-8 mb-2">
                <div className="w-16 h-16 rounded-full border-4 border-surface overflow-hidden bg-surface shadow-md">
                  <Avatar
                    url={profile.avatar.medium}
                    // Nullable only because the DM/reaction serializers have
                    // not adopted `displayName()` yet; this route always
                    // writes `Bruger {id}` rather than a label-less row (O-21).
                    name={profile.name ?? ""}
                    size="full"
                  />
                </div>
              </div>
              <h3 className="text-base font-bold text-foreground truncate flex items-center gap-1.5">
                <span className="truncate">{profile.name}</span>
                {profile.isBot && <BotBadge />}
              </h3>
              {(profile.mitIdVerifiedAt || roleBadge) && (
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  {roleBadge && (
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${roleBadge.className}`}
                    >
                      {roleBadge.label}
                    </span>
                  )}
                  {profile.mitIdVerifiedAt && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-300 text-xs font-medium border border-emerald-200 dark:border-emerald-800">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      Verificeret med MitID
                    </span>
                  )}
                </div>
              )}
              {affiliation && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {/* The chip reads a name and a slug, which `GroupSummary`
                      already has — the shape conversion that stood here is
                      gone. One affiliation, not a list: §2.2 #12 carries the
                      single group the person admins above this one, which is
                      what the old array only ever had one of. */}
                  <AffiliationChip affiliation={affiliation} />
                </div>
              )}
              {profile.bioText && (
                <p className="text-sm text-muted-foreground mt-2 line-clamp-3">
                  {profile.bioText}
                </p>
              )}
              {(memberSince || groupSince) && (
                <div className="mt-3 pt-3 border-t border-border space-y-0.5">
                  {memberSince && (
                    <p className="text-xs text-subtle-foreground">
                      Medlem siden {memberSince}
                    </p>
                  )}
                  {groupSince && (
                    <p className="text-xs text-subtle-foreground">
                      Medlem af gruppen siden {groupSince}
                    </p>
                  )}
                </div>
              )}
              {canMessage && (
                <div className="mt-3 pt-3 border-t border-border">
                  <button
                    type="button"
                    onClick={handleSendMessage}
                    disabled={dmBusy}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-foreground bg-muted rounded-lg hover:bg-surface-2 transition-colors disabled:opacity-50"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                    {isRequest ? "Svar på anmodning" : "Send besked"}
                  </button>
                  {dmError && (
                    <p className="text-xs text-destructive mt-1">{dmError}</p>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * `userId` is nullable, and null means "render the trigger, no card".
 *
 * Null is not "we could not work out who this is" but "there is nobody":
 * `author_id` is `ON DELETE SET NULL`, and an anonymous post has no author id.
 * Neither has a profile to open, so the trigger renders bare. Every other case
 * has an id, on the legacy wire and on /api/v1 alike, and gets a card.
 */
export function ProfileHoverCard({
  userId,
  children,
  categoryId,
}: {
  userId: number | null;
  children: React.ReactNode;
  // As on the panel above: what makes the card contextual.
  categoryId?: number;
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<HoverCardCoords | null>(null);
  const triggerRef = useRef<HTMLSpanElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  /** Where Escape and Shift+Tab put focus back: whatever held it on open. */
  const focusedTrigger = useRef<HTMLElement | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  const position = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    setCoords(computeHoverCardPosition(el));
  }, []);

  const scheduleOpen = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    // A click on a name fires mouseEnter AND focus; without this the first
    // timer is orphaned rather than replaced.
    if (openTimer.current) clearTimeout(openTimer.current);
    if (open) return;
    openTimer.current = setTimeout(() => {
      position();
      setOpen(true);
    }, HOVER_CARD_OPEN_DELAY);
  }, [open, position]);

  const scheduleClose = useCallback(() => {
    if (openTimer.current) clearTimeout(openTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), HOVER_CARD_CLOSE_DELAY);
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const close = useCallback(() => setOpen(false), []);
  const restoreFocus = useCallback(() => focusedTrigger.current?.focus(), []);
  const { onKeyDown } = useHoverCardKeyboard({ open, cardRef, restoreFocus, close });

  /**
   * The card is reachable by keyboard, and still portalled (OSO-458).
   *
   * `createPortal` puts the card at the end of `document.body`, so the tab order
   * runs straight past it: Tab from the trigger went to whatever follows the
   * trigger in the document, firing `onBlur` and closing the card, which left
   * the "Send besked" button mouse-only.
   *
   * The portal is not the thing to change. It is on the `createPortal` allowlist
   * in `next-app/ratchets.json` because the card is `position: absolute` in page
   * coordinates (`computeHoverCardPosition` adds `window.scrollX/Y`), so
   * rendering it inline would trap it in whatever clipping and stacking context
   * the trigger sits in; fixing that means moving to `useAnchoredPosition` and
   * `position: fixed` first, which is a bigger change than this bug.
   *
   * The hand-off is `useHoverCardKeyboard`, shared with the other trigger —
   * `PostHtml`, off a raw `.mention` anchor. It used to live here, which is how
   * that trigger came to render a `role="dialog"` panel with no way in or out
   * (OSO-512). A promise the shared panel makes cannot be kept by one caller.
   */
  /**
   * Focus moving from the trigger INTO the card is not focus leaving. Without
   * this the hand-off above would close the card on its way in.
   */
  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      const next = e.relatedTarget as Node | null;
      if (
        next &&
        (cardRef.current?.contains(next) || triggerRef.current?.contains(next))
      ) {
        return;
      }
      scheduleClose();
    },
    [scheduleClose],
  );

  const handleFocus = useCallback(
    (e: React.FocusEvent) => {
      // Only the TRIGGER's own focus opens the card; focus arriving inside the
      // card is the hand-off, and must not restart the timer or overwrite the
      // element we return to.
      if (cardRef.current?.contains(e.target as Node)) return;
      focusedTrigger.current = e.target as HTMLElement;
      scheduleOpen();
    },
    [scheduleOpen],
  );

  if (userId === null) return <>{children}</>;

  return (
    <span
      ref={triggerRef}
      className="inline-flex"
      onMouseEnter={scheduleOpen}
      onMouseLeave={scheduleClose}
      // Focus takes the same delay as hover. Opening immediately meant tabbing
      // across a surface of many triggers — a poll's voters are 4×10 of them
      // (OSO-397) — flashed a 300px card at every stop. With the delay a
      // pass-through opens nothing and only stopping on someone does.
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={onKeyDown}
    >
      {children}
      {open &&
        coords &&
        typeof document !== "undefined" &&
        createPortal(
          <ProfileHoverCardPanel
            userId={userId}
            categoryId={categoryId}
            coords={coords}
            panelRef={cardRef}
            onMouseEnter={() => {
              if (closeTimer.current) clearTimeout(closeTimer.current);
            }}
            onMouseLeave={scheduleClose}
          />,
          document.body,
        )}
    </span>
  );
}
